<template>
    <div>
        <SpinnerLoading v-if="users === undefined" class="mt-50" />
        <div class="table-responsive" v-else>
            <table class="table text-nowrap align-middle mb-0">
                <thead>
                    <tr>
                        <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                            ID
                        </th>
                        <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                            {{ $t('form.first_name.label') }}
                        </th>
                        <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                            {{ $t('form.last_name.label') }}
                        </th>
                        <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                            {{ $t('form.role_in_department.label') }}
                        </th>
                        <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                            {{ $t('form.email.label') }}
                        </th>
                        <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                            {{ $t('form.phone.label') }}
                        </th>
                        <th scope="col"
                            class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                        </th>
                    </tr>
                </thead>
                <tbody class="linked">
                    <!-- PATIENT ROW -->
                    <tr v-for="user in users" :key="user.id">
                        <th class="shadow-none lh-1 fw-medium text-body-tertiary title" @click="show(user)">
                            <div class="d-flex align-items-center justify-content-start">
                                {{ user.id }}
                            </div>
                        </th>
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary" @click="show(user)">
                            {{ user.firstname }}
                        </td>
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary" @click="show(user)">
                            {{ user.lastname }}
                        </td>
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary" @click="show(user)">
                            {{ user.role_in_department }}
                        </td>
                        <td class="shadow-none lh-1 fw-medium text-primary" @click="show(user)">
                            {{ user.email }}
                        </td>
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary" @click="show(user)">
                            {{ user.phone_number }}
                        </td>
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                            <div class="dropdown position-static">
                                <button class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                                    type="button" data-bs-toggle="dropdown" aria-expanded="false"
                                    data-bs-boundary="viewport">
                                    <i class="flaticon-dots"></i>
                                </button>
                                <ul class="dropdown-menu">
                                    <li>
                                        <router-link :to="{
                                            path: `/user/${user.id}`
                                        }" class="dropdown-item d-flex align-items-center">
                                            <i class="ph ph-eye lh-1 me-8 position-relative top-1"></i>
                                            {{ $t('cta.view') }}
                                        </router-link>
                                    </li>
                                    <li>
                                        <router-link
                                            :to="{ path: `/user/${user.id}/edit` }"
                                            class="dropdown-item d-flex align-items-center">
                                            <i class="ph-duotone ph-pencil lh-1 me-8 position-relative top-1"></i>
                                            {{ $t('cta.edit') }}
                                        </router-link>
                                    </li>
                                    <li>
                                        <button class="dropdown-item d-flex align-items-center"
                                            v-on:click="(e) => recoveryPassword(user, e)">
                                            <span v-if="recovery_loading"
                                                class="spinner-border spinner-border-xs border-1 me-8" role="status"
                                                aria-hidden="true"></span>
                                            <i v-else
                                                class="ph ph-arrow-clockwise lh-1 me-8 position-relative top-1"></i>
                                            {{ $t('cta.recovery_password') }}
                                        </button>
                                    </li>
                                    <li>
                                        <button class="dropdown-item d-flex align-items-center"
                                            v-on:click="confirmDeleteUser(user)">
                                            <i class="ph ph-trash lh-1 me-8 position-relative top-1"></i>
                                            {{ $t('cta.delete') }}
                                        </button>
                                    </li>
                                </ul>

                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- PAGINATION -->
        <TablePagination :count="users?.length ?? 0" :page="currentPage" :total="totalUsers" :pages="lastPage"
            @change="setPage($event)" />
    </div>
    <AlertModal id="recovery_modal" :title="$t('feature.auth.$recovery_user_password.title')"
        :message="recovery_modal_message" />
    <ConfirmModal @confirm="deleteUser()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import API from "../../../services/api/api.service";
import { Modal } from "bootstrap";
import { RoleInfo, UserInfo } from "@/utils/models/user.model";
import TablePagination from "@/components/FK/TablePagination.vue";
import AlertModal from "@/components/Widgets/AlertModal.vue";

const PAGE_SIZE = 10;

export default defineComponent({
    name: "ZoneHCPList",
    props: {
        // center_id: { type: [String, Number], required: true },
        zone_id: { type: [String, Number], required: true }
    },
    emits: ['delete'],
    data() {
        return {
            users: undefined,
            currentPage: 1,
            lastPage: 1,
            perPage: PAGE_SIZE,
            totalUsers: 0,
            confirmModal: null,
            selectedUser: null,
            roles: this.$route.meta.roles,
            roleId: this.$route.query.role_id ?? -1,
            recovery_loading: false,
            recovery_modal: null,
            recovery_modal_message: '',
        } as {
            users?: UserInfo[],
            currentPage: number,
            totalUsers: number,
            lastPage: number,
            perPage: number,
            confirmModal: Modal | null,
            selectedUser: UserInfo | null;
            roles?: RoleInfo[];
            roleId: number;
            recovery_loading: boolean;
            recovery_modal: Modal | null;
            recovery_modal_message: string;
        }
    },
    mounted() {
        this.recovery_modal = new Modal('#recovery_modal', {});
        var queryIndex = parseInt(this.$route.query.page as string ?? '1');
        if (queryIndex > 0) {
            this.currentPage = queryIndex;
        }
        this.loadUsers(this.currentPage);
        this.confirmModal = new Modal('#confirmModal', {});
        // this.loadRoles();
    },
    computed: {
        role(): number | undefined {
            return this.roleId > 0 ? this.roleId : undefined;
        }
    },
    methods: {
        loadUsers(page: number) {
            this.users = undefined;
            API.getZoneHCPs({ zone_id: this.zone_id, page: page }).then((res) => {
                this.users = res.data.data;
                this.totalUsers = res.data.total;
                this.lastPage = res.data.last_page;
            })
        },
        setPage(page: number) {
            this.currentPage = page;
            this.$router.replace({ query: { page: this.currentPage, role_id: this.role } });
            this.loadUsers(page);
        },
        confirmDeleteUser(user: UserInfo) {
            this.selectedUser = user;
            this.$data.confirmModal?.show();
        },
        loadRoles() {
            API.getRoles().then((response) => {
                this.roles = response.data?.data ?? [];
            })
        },
        deleteUser(): void {
            API.deleteUser(this.selectedUser?.id!).then((response) => {
                if (response.data.success === true) {
                    this.selectedUser = null;
                    this.setPage(1);
                    this.$emit('delete');
                }
            }).finally(() => {
                console.log("DELETED_PATIENT_ID", this.selectedUser?.id);
            })
        },
        setFilter() {
            console.log(this.roleId);
            this.setPage(1);
        },
        show(user: UserInfo) {
            this.$router.push({ path: `/user/${user.id}` });
        },
        recoveryPassword(user: UserInfo, e: Event) {
            e.stopPropagation();
            this.recovery_loading = true;
            API.recoveryUserPassword(user.id).then((response) => {
                if (response.data.success === true) {
                    this.recovery_modal_message = this.$t('feature.auth.$recovery_user_password.success_message');
                    console.debug('recovery_password', user.id);
                } else {
                    this.recovery_modal_message = this.$t('feature.auth.$recovery_user_password.error_message');
                }
            }).catch((error) => {
                this.recovery_modal_message = this.$t('feature.auth.$recovery_user_password.error_message');
            }).finally(() => {
                this.recovery_loading = false;
                this.recovery_modal?.show();
            });
        }
    },
    components: {
        ConfirmModal,
        AlertModal,
        SpinnerLoading,
        TablePagination
    },
});
</script>